/**
 * @description: 虚拟滚动列表
 */
import { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { ForwardedRef, ReactElement, RefAttributes } from 'react';
import cn from 'classnames';
import { useMemoizedFn } from 'ahooks';
import { useInView } from 'react-intersection-observer';
import { useVirtualizer } from '@tanstack/react-virtual';

export interface VirtualListInstance {
  scrollToIndex: (
    index: number,
    opts?: {
      align?: 'start' | 'center' | 'end' | 'auto';
      behavior?: 'smooth' | 'auto';
    }
  ) => void;
  scrollToOffset: (offset: number, opts?: { behavior?: 'smooth' | 'auto' }) => void;
  /** 强制重新计算所有 item 的估算高度（estimateSize 依赖项变化时调用） */
  measure: () => void;
}

export interface VirtualListItemProps<T> {
  data: T[];
  initialOffset: number;
  setScrollOffset: (offset: number) => void;
  estimateSize: (index: number) => number;
  renderItem?: (item: T, index: number) => React.ReactNode;
  getScrollElement: () => HTMLElement; // 非常重要，获取滚动容器元素，确保容器有固定高度且overflow-y-auto，否则列表将降级为普通列表
  gap?: number;
  classNames?: {
    scrollRoot?: string;
    tempContainer?: string;
    itemContainer?: string;
  };
  onBottomReached?: () => void;
  onTopReached?: () => void;
  topSlot?: React.ReactNode;
  bottomSlot?: React.ReactNode;
}

function VirtualListItemInner<T>(props: VirtualListItemProps<T>, ref: ForwardedRef<VirtualListInstance>) {
  const {
    data,
    initialOffset,
    setScrollOffset,
    estimateSize,
    renderItem,
    getScrollElement,
    gap,
    classNames,
    onBottomReached,
    onTopReached,
    topSlot,
    bottomSlot,
  } = props;
  const tempRef = useRef<HTMLDivElement>(null);

  const { ref: bottomRef, inView: bottomInView } = useInView({
    rootMargin: '0px 0px 200px 0px', // 底部边距200px，提前触发
    threshold: 0,
    triggerOnce: false,
  });

  const { ref: topRef, inView: inTopView } = useInView({
    rootMargin: '0px 0px 200px 0px', // 底部边距200px，提前触发
    threshold: 0,
    triggerOnce: false,
  });

  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement,
    estimateSize,
    gap,
    initialOffset,
    onChange(instance) {
      setScrollOffset(instance.scrollOffset || 0);
    },
  });

  useImperativeHandle(
    ref,
    (): VirtualListInstance => ({
      scrollToIndex: (index, opts) => rowVirtualizer.scrollToIndex(index, opts),
      scrollToOffset: (offset, opts) => rowVirtualizer.scrollToOffset(offset, opts),
      measure: () => rowVirtualizer.measure(),
    }),
    [rowVirtualizer]
  );

  const handleTopReached = useMemoizedFn(() => {
    if (onTopReached) {
      onTopReached();
    }
  });

  const handleBottomReached = useMemoizedFn(() => {
    if (onBottomReached) {
      onBottomReached();
    }
  });

  useEffect(() => {
    if (bottomInView) {
      handleBottomReached();
    }
  }, [bottomInView, handleBottomReached]);

  useEffect(() => {
    if (inTopView) {
      handleTopReached();
    }
  }, [inTopView, handleTopReached]);

  return (
    <div className={cn(classNames?.scrollRoot)} ref={tempRef}>
      <div ref={topRef} />
      {topSlot}
      <div
        className={cn(classNames?.tempContainer)}
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map(virtualItem => {
          return (
            <div
              className={cn(classNames?.itemContainer)}
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: virtualItem.start,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                // paddingBottom: 12,
                // transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {renderItem ? renderItem(data[virtualItem.index], virtualItem.index) : null}
            </div>
          );
        })}
      </div>
      {bottomSlot}
      <div ref={bottomRef} />
    </div>
  );
}

const VirtualListItem = forwardRef(VirtualListItemInner) as <T>(
  props: VirtualListItemProps<T> & RefAttributes<VirtualListInstance>
) => ReactElement | null;

export default VirtualListItem;
